/**
 * Mergeport Ordering
 * ## Introduction    Mergeport Ordering is a channel manager for online ordering and delivery services. With a single interface, we establish a connection between a network of POS systems and multiple service providers in a standardized format.    ## Access details and developer account   If not already done, **contact us** for a developer account. You will receive a ```providerId``` as identification of your platform as well as a ```restaurantId``` of  demo restaurant. In addition, we will provide you with credentials to log in to our MERGEPORT Controller interface. The ```restaurantId``` changes for each restaurant.     ## Data flow   MERGEPORT provides the possibility to synchronize orders and articles with a POS system. The POS system linked to the specified ```restaurantId``` will be notified in realtime about incoming orders or cancellations and process them correspondingly. MERGEPORT will deliver the orders also if the POS system is not available at the time of creation.    ### Order status  Once the order has been dispatched, the POS can process it. First, the POS will set the status to ```fetchedByPOS``` to mark it as read. After processing, the POS is supposed to update the status to ```acceptedByPOS``` or ```rejectedByPOS```. ```acceptedByPOS``` means, the gastronomy will take care about the delivery. You can check the status by either fetching the order via the HTTP GET endpoint or by setting a web hook for the notification. Please contact the MERGEPORT admins upon provider creation for this feature or use the corresponding endpoint to set the web hook (callbackUrl) via API..     ### Cancellation   Should the order be cancelled before the POS processes the order, the order will be terminated and not forwarded to the POS anymore. If the POS has already started the processing, it will be assigned with a request to cancel the order.    ### Payment   What is specified in the ```payment``` object of the payment defines how the POS handles the order. The parameter ```payOnDelivery``` defines whether the order is already paid (```payOnDelivery=false```) or still needs to be paid (```payOnDelivery=true```). The ```brand```attribute states where the payment came from, e.g. paypal and the ```type``` specifies if it is an online (```CARD```) or offline (```CASH```) payment.    ### Prices and currencies   All prices are expressed as a structured object, with the currency code, e.g. \"EUR\" and the value in the atomic part of that currency, typically \"cent\". 3� will be represented as 300.    ### Menu, categories and items   Depending on the implementation of the POS, MERGEPORT stores a shadow copy of the products (internally also called ```items```), menu categories of the POS. The POS is supposed to update the products at MERGEPORT upon every change. Within the MERGEPORT controller, the gastronomer is able to enrich the products, menu and categories with meta information such as localized names and descriptions as well as images. By requesting the menu of the restaurant, the provider can import the (enriched) products. Almost any kind of variation of products can be represented by this structure. The ```type``` of a product distinguishes products and toppings. Both of which can be associated to menus and categories.  With the parameter ```parentIds```, products/toppings can be linked to one or multiple items in a hierarchical order.  To post an order, the provider needs to set the item ID of the product as the ```remoteCode``` of the product (see POST Order). The products of an order are included in the ```products``` object in a hierarchical order. For example, toppings can be included as a child of the parent product.    ### Matching of the products   When an order is placed, the POS will try to match the contained products with the articles stored in its database. If a ```remoteCode``` is set by the platform, the POS will match it against its interal product ids. If this process fails, it will try to match a product by name or create a dummy fallback product. Additionally, if the implementation allows it, the gastronomer is able to map the products of the POS with those of the platform in the MERGEPORT Controller.  ## Responses   Typically, our responses are being returned with the **HTTP status codes** ```200``` **OK**, ```400``` **Bad Request**, ```401``` **Unauthorized**, ```403``` **Forbidden**. We have three response types, you can easily check the response for the following fields&#58;   - ```[data]``` - for read operations. The response contains an object or array of the requested data   - ```success``` - for generic operations. The success property contains a message describing the operation result.   - ```error``` - for minor errors. The error property contains a code and a message  ## Request validation   All requests will be validated. The following cases will lead to a validation error:   - Missing required properties   - Unknown properties in the request body   - Invalid data format.    Important&#58; date-time fields and parameters must be formatted as UTC ISO 8601 strings, e.g. '2000-01-01T10:10:10.123Z'. 
 *
 * OpenAPI spec version: 4.0.8
 * Contact: hello@mergeport.com
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 *//* tslint:disable:no-unused-variable member-ordering */

import { Inject, Injectable, Optional }                      from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams,
         HttpResponse, HttpEvent }                           from '@angular/common/http';
import { CustomHttpUrlEncodingCodec }                        from '../encoder';

import { Observable }                                        from 'rxjs';

import { InlineResponse2004 } from '../model/inlineResponse2004';
import { RestaurantIdReceiptsBody } from '../model/restaurantIdReceiptsBody';

import { BASE_PATH, COLLECTION_FORMATS }                     from '../variables';
import { Configuration }                                     from '../configuration';


@Injectable()
export class PaymentsService {

    protected basePath = 'https://ordering.mergeport.com/v4/';
    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();

    constructor(protected httpClient: HttpClient, @Optional()@Inject(BASE_PATH) basePath: string, @Optional() configuration: Configuration) {
        if (basePath) {
            this.basePath = basePath;
        }
        if (configuration) {
            this.configuration = configuration;
            this.basePath = basePath || configuration.basePath || this.basePath;
        }
    }

    /**
     * @param consumes string[] mime-types
     * @return true: consumes contains 'multipart/form-data', false: otherwise
     */
    private canConsumeForm(consumes: string[]): boolean {
        const form = 'multipart/form-data';
        for (const consume of consumes) {
            if (form === consume) {
                return true;
            }
        }
        return false;
    }


    /**
     * Cancel a payment
     * This action submits a cancellation of a payment to request a POS receipt cancellation.  WebSocket action: **CancelProviderPayment**
     * @param providerId provider ID from mergeport
     * @param restaurantId remote restaurantId provided by Mergeport
     * @param body Object of Pos PaymentCancellation
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public cancelProviderPayment(providerId: string, restaurantId: string, body?: any, observe?: 'body', reportProgress?: boolean): Observable<any>;
    public cancelProviderPayment(providerId: string, restaurantId: string, body?: any, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<any>>;
    public cancelProviderPayment(providerId: string, restaurantId: string, body?: any, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<any>>;
    public cancelProviderPayment(providerId: string, restaurantId: string, body?: any, observe: any = 'body', reportProgress: boolean = false ): Observable<any> {

        if (providerId === null || providerId === undefined) {
            throw new Error('Required parameter providerId was null or undefined when calling cancelProviderPayment.');
        }

        if (restaurantId === null || restaurantId === undefined) {
            throw new Error('Required parameter restaurantId was null or undefined when calling cancelProviderPayment.');
        }


        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
            'application/json'
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected != undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        return this.httpClient.request<any>('patch',`${this.basePath}/hooks/main/${encodeURIComponent(String(providerId))}/${encodeURIComponent(String(restaurantId))}/receipts`,
            {
                body: body,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Show a table
     * Shows the specified table with its items  WebSocket action: **GetMainTable**
     * @param providerId provider ID from mergeport
     * @param restaurantId remote restaurantId provided by Mergeport
     * @param id unique id of the table
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getMainTable(providerId: string, restaurantId: string, id: string, observe?: 'body', reportProgress?: boolean): Observable<InlineResponse2004>;
    public getMainTable(providerId: string, restaurantId: string, id: string, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<InlineResponse2004>>;
    public getMainTable(providerId: string, restaurantId: string, id: string, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<InlineResponse2004>>;
    public getMainTable(providerId: string, restaurantId: string, id: string, observe: any = 'body', reportProgress: boolean = false ): Observable<any> {

        if (providerId === null || providerId === undefined) {
            throw new Error('Required parameter providerId was null or undefined when calling getMainTable.');
        }

        if (restaurantId === null || restaurantId === undefined) {
            throw new Error('Required parameter restaurantId was null or undefined when calling getMainTable.');
        }

        if (id === null || id === undefined) {
            throw new Error('Required parameter id was null or undefined when calling getMainTable.');
        }

        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
            'application/json'
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [
        ];

        return this.httpClient.request<InlineResponse2004>('get',`${this.basePath}/hooks/main/${encodeURIComponent(String(providerId))}/${encodeURIComponent(String(restaurantId))}/tables/${encodeURIComponent(String(id))}`,
            {
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Endpoint to set an URL to get receipts from the POS
     * POST an URL to get receipts from the POS linked to the restaurant  WebSocket action: **MainSetReceiptURL**
     * @param providerId provider ID from mergeport
     * @param restaurantId online Restaurant ID
     * @param body 
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public mainSetReceiptURL(providerId: string, restaurantId: string, body?: RestaurantIdReceiptsBody, observe?: 'body', reportProgress?: boolean): Observable<any>;
    public mainSetReceiptURL(providerId: string, restaurantId: string, body?: RestaurantIdReceiptsBody, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<any>>;
    public mainSetReceiptURL(providerId: string, restaurantId: string, body?: RestaurantIdReceiptsBody, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<any>>;
    public mainSetReceiptURL(providerId: string, restaurantId: string, body?: RestaurantIdReceiptsBody, observe: any = 'body', reportProgress: boolean = false ): Observable<any> {

        if (providerId === null || providerId === undefined) {
            throw new Error('Required parameter providerId was null or undefined when calling mainSetReceiptURL.');
        }

        if (restaurantId === null || restaurantId === undefined) {
            throw new Error('Required parameter restaurantId was null or undefined when calling mainSetReceiptURL.');
        }


        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected != undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        return this.httpClient.request<any>('post',`${this.basePath}/hooks/main/${encodeURIComponent(String(providerId))}/${encodeURIComponent(String(restaurantId))}/receipts`,
            {
                body: body,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Create a payment
     * This action submits a payment to request a POS receipt.  WebSocket action: **SetProviderPayment**
     * @param providerId provider ID from mergeport
     * @param restaurantId remote restaurantId provided by Mergeport
     * @param body 
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public setProviderPayment(providerId: string, restaurantId: string, body?: any, observe?: 'body', reportProgress?: boolean): Observable<any>;
    public setProviderPayment(providerId: string, restaurantId: string, body?: any, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<any>>;
    public setProviderPayment(providerId: string, restaurantId: string, body?: any, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<any>>;
    public setProviderPayment(providerId: string, restaurantId: string, body?: any, observe: any = 'body', reportProgress: boolean = false ): Observable<any> {

        if (providerId === null || providerId === undefined) {
            throw new Error('Required parameter providerId was null or undefined when calling setProviderPayment.');
        }

        if (restaurantId === null || restaurantId === undefined) {
            throw new Error('Required parameter restaurantId was null or undefined when calling setProviderPayment.');
        }


        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
            'application/json'
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected != undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        return this.httpClient.request<any>('put',`${this.basePath}/hooks/main/${encodeURIComponent(String(providerId))}/${encodeURIComponent(String(restaurantId))}/receipts`,
            {
                body: body,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

}
